import { CalendlyExpert } from '@/features/experts/types/CalendlyExpert';
import { TimeSlot } from '@/features/experts/types/TimeSlot';
import { useUserStore } from '@/utils/stores/useUserStore';
import { ArrowLeft } from 'lucide-react';
import { VenereButton } from '../../shared/ui';
import { useVenereCalendlyUrl } from '../hooks/useVenereCalendlyUrl';
import { useVenereAppointments } from '../hooks/useVenereConsults';
import { useVenereExpertBooking } from '../hooks/useVenereExpertBooking';
import { formatDateToYYYYMMDD } from '../utils/dateFormatters';

interface VenereExpertBookingCalendarProps {
  expert: CalendlyExpert;
  onBookClick: (expert: CalendlyExpert) => void;
}

export const VenereExpertBookingCalendar: React.FC<VenereExpertBookingCalendarProps> = ({
  expert,
  onBookClick,
}) => {
  const { userData } = useUserStore();
  const { refetch: refetchAppointments } = useVenereAppointments();
  
  const {
    currentMonth,
    monthAvailabilities,
    isLoadingMonth,
    selectedDate,
    selectedSlot,
    showCalendlyWidget,
    getDaysInMonth,
    isToday,
    isPastDate,
    canNavigateToPreviousMonth,
    changeMonth,
    handleDayClick,
    handleSlotSelect,
    handleBook,
    handleCalendlyClose,
    formatTimeSlot,
  } = useVenereExpertBooking({ 
    expert, 
    onBookClick,
    onBookingComplete: refetchAppointments 
  });

  const { fullUrl } = useVenereCalendlyUrl({
    expert,
    selectedDate,
    selectedSlot,
    userData: {
      email: userData?.email || '',
      name: userData?.name || '',
      familyName: userData?.familyName || '',
      phoneNumber: userData?.phoneNumber || '',
    }
  });

  // Enhanced Calendly close handler that refetches appointments
  const handleCalendlyCloseWithRefetch = () => {
    // Refetch appointments after booking
    refetchAppointments();
    // Call the original close handler
    handleCalendlyClose();
  };

  return (
    <div className="w-full border-t border-solid border-gray-200 p-6">
      {!showCalendlyWidget ? (
        <div className="flex flex-col gap-4">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <VenereButton
              variant="back"
              size="sm"
              onClick={() => changeMonth(-1)}
              disabled={isLoadingMonth || !canNavigateToPreviousMonth()}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span className="hidden md:block">Mese precedente</span>
            </VenereButton>
            <h3 className="venere-text-primary">
              {currentMonth.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
            </h3>
            <VenereButton
              variant="forward"
              size="sm"
              onClick={() => changeMonth(1)}
              disabled={isLoadingMonth}
            />
          </div>

          {/* Calendar Grid */}
          <div className="relative">
            {isLoadingMonth && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-50">
                <div className="w-8 h-8 border-4 border-solid border-venere-royal border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <div className="grid grid-cols-7 gap-2 mb-4">
              {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => (
                <div key={day} className="text-center font-medium text-sm text-gray-500">
                  {day}
                </div>
              ))}
              {getDaysInMonth(currentMonth).map((date: Date | null, index: number) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="p-2" />;
                }

                const dateStr = formatDateToYYYYMMDD(date);
                const hasAvailability = monthAvailabilities[dateStr]?.length > 0;
                const isPast = isPastDate(date);
                const isSelected = selectedDate === dateStr;

                return (
                  <button
                    key={dateStr}
                    onClick={() => handleDayClick(date, hasAvailability)}
                    className={`p-2 rounded-md text-center relative transition-all duration-200 ${
                      isToday(date)
                        ? 'ring-2 ring-venere-royal ring-offset-2'
                        : isPast
                        ? 'bg-gray-50 text-gray-400 opacity-50'
                        : hasAvailability
                        ? isSelected
                          ? 'bg-venere-royal/10 hover:bg-venere-royal/20 text-venere-royal border-2 border-venere-royal'
                          : 'bg-green-50 hover:bg-green-100 text-green-700 border-2 border-venere-royal'
                        : 'bg-gray-50 text-gray-400'
                    }`}
                    disabled={isLoadingMonth || isPast || !hasAvailability}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Slots Selection */}
          {selectedDate && monthAvailabilities[selectedDate] && (
            <div className="mt-4 p-4 border border-gray-200 rounded-lg">
              <h4 className="venere-text-primary mb-4">
                {new Date(selectedDate).toLocaleDateString('it-IT', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long'
                })}
              </h4>
              <div className="text-sm text-gray-600 mb-2">
                Debug: {monthAvailabilities[selectedDate]?.length || 0} slots available
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {monthAvailabilities[selectedDate].map((slot: TimeSlot, index: number) => (
                  <button
                    key={index}
                    onClick={() => handleSlotSelect(slot)}
                    className={`p-3 rounded-md text-center transition-all duration-200 ${
                      selectedSlot === slot
                        ? 'bg-venere-royal text-white'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    {formatTimeSlot(slot)}
                  </button>
                ))}
              </div>
              {selectedSlot && (
                <div className="mt-4 flex justify-end">
                  <VenereButton
                    onClick={handleBook}
                    variant="primary"
                  >
                    Continua con Calendly
                  </VenereButton>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="h-[600px]">
          <iframe
            src={fullUrl}
            width="100%"
            height="100%"
            frameBorder="0"
            title="Calendly Scheduling Page"
            onLoad={() => {
              // Listen for Calendly events
              window.addEventListener('message', (event) => {
                if (event.data.event === 'calendly.event_scheduled') {
                  // Refetch appointments when booking is completed
                  setTimeout(() => {
                    refetchAppointments();
                  }, 1000); // Small delay to ensure backend has processed the booking
                }
              });
            }}
          />
        </div>
      )}
    </div>
  );
}; 